import { useSelector } from 'react-redux'
import { ImageSetData, LabeledImageItem } from '@/store/slices/types'
import { PopoverWidthType } from '../../FloatingInspectorButton/SharedComponents/Popover/WrapPopover'
import { CategoryItem } from '../../FloatingInspectorButton/SharedComponents/CategoryHorizontalScroller'
import { ButtonListObject } from '../../FloatingInspectorButton/SharedComponents/Popover/ButtonsListPopover'
import { selectEditorState } from '@/store/slices/editor/selector'
import { SuggestionsButtonSet } from '@/constants/app-options'
import {
  useAIFashionButtonSetData,
  getAIFashionButtons,
  type AIFashionButtonSetData,
} from '@/scenes/AIStudioLite/buttonSets/aiStudioAIFashionButtonSet'
import { aiStudioVideoButtonSet } from '@/scenes/AIStudioLite/buttonSets/aiStudioVideoButtonSet'
import { fabric } from 'fabric'
import type { AIFashionButtonSetOverride } from '@/scenes/AIStudioLite/AIFashionController'

// ─── Shared types ────────────────────────────────────────────────────────────

export type SuggestionListPopoverItem = {
  text: string
  value: string
  subtitle?: string
  icon?: JSX.Element
  onClick?: () => void
  closePopoverOnClick?: boolean
}

export type { LabeledImageItem, ImageSetData }

/**
 * UI-only extension of LabeledImageItem. onClick is intentionally NOT part of
 * the stored/API LabeledImageItem to keep Redux state serializable.
 */
export type ClickableLabeledImageItem = LabeledImageItem & {
  onClick?: () => void
  categoryId?: string
  topHex?: string
  bottomHex?: string
}

export type SuggestionButtonPopover =
  | {
      designType: 'list'
      title?: string
      width?: PopoverWidthType
      height?: number
      maxHeight?: number
      items: SuggestionListPopoverItem[]
      categories?: CategoryItem[]
    }
  | {
      designType: 'grid'
      title?: string
      width?: PopoverWidthType
      height?: number
      maxHeight?: number
      columns: number
      aspectRatio?: number | string
      /** Number of skeleton ghost items shown while tiles are loading. Defaults to columns * 4. */
      skeletonCount?: number
      items: ClickableLabeledImageItem[]
      categories?: CategoryItem[]
      onLoadMore?: (onComplete?: () => void) => void
      isLoadingMore?: boolean
      loadMoreSkeletonCount?: number
      skeletonVariant?: 'default' | 'gradient'
      /** When true, scrolls the scroll container to the bottom on mount. */
      scrollToBottom?: boolean
    }
  | {
      designType: 'list_white'
      title?: string
      width?: PopoverWidthType
      minWidth?: number
      maxWidth?: number
      height?: number
      maxHeight?: number
      items: ButtonListObject[]
      categories?: CategoryItem[]
    }

export type SuggestionButton = {
  title: string
  value?: string
  icon?: JSX.Element
  imageSrc?: string
  buttonStyle?: 'default' | 'imageInCircle'
  onClick?: () => void
  popover?: SuggestionButtonPopover
}

/**
 * Protocol for a self-contained button set.
 * Each set co-locates its key, data-fetching hook, and button factory.
 * useData receives activeObject so each set owns its own data fetching.
 * Runtime-specific controller lookup belongs in each adapter. Shared button
 * data/building logic must stay context-free so AI Studio can consume it directly.
 *
 * To add a new set:
 *  1. Create buttonSets/<name>ButtonSet.ts implementing ButtonSetDefinition<YourData>
 *  2. Import it and add one `const data = <name>ButtonSet.useData(activeObject)` line in useSuggestionButtons
 *  3. Add a matching `if (activeKey === '<key>')` return branch
 *  4. Add '<key>' to SuggestionsButtonSet in app-options.ts
 */
export type ButtonSetDefinition<TData> = {
  key: NonNullable<SuggestionsButtonSet>
  useData: (activeObject?: fabric.Object | null, override?: AIFashionButtonSetOverride) => TData
  getButtons: (data: TData, onActionSelect: (action: string) => void, triggerGeneration?: (loadingLabel?: string) => void) => SuggestionButton[]
}

// ─── Helper (exported so button sets can use it) ──────────────────────────────

export const withOnClick = <T>(
  items: T[],
  getValue: (item: T) => string,
  onActionSelect: (action: string) => void
): (T & { onClick: () => void })[] =>
  items.map((item) => ({ ...item, onClick: () => onActionSelect(getValue(item)) }))

// ─── Master hook ──────────────────────────────────────────────────────────────
// Call every set's useData hook unconditionally to preserve hook order.
// Add one line here for each new entry added to aiFashion pattern.

export function useSuggestionButtons(
  onActionSelect: (action: string) => void,
  activeObject?: fabric.Object | null,
  triggerGeneration?: (loadingLabel?: string) => void,
  aiFashionOverride?: AIFashionButtonSetOverride,
): SuggestionButton[] {
  const controller = aiFashionOverride?.controller ?? null
  const targetId = aiFashionOverride?.targetId ?? ((activeObject as any)?.id as string | undefined)
  const isReady = aiFashionOverride ? !!aiFashionOverride.controller : !!activeObject
  const aiFashionData: AIFashionButtonSetData = useAIFashionButtonSetData({ controller, targetId, isReady })
  const aiStudioVideoData = aiStudioVideoButtonSet.useData(activeObject, aiFashionOverride)

  const editorState = useSelector(selectEditorState)
  const activeKey = editorState.suggestionsButtonSet

  // When running outside the editor (AI Studio), force-evaluate the AI Fashion set:
  // the editor's `editorState.suggestionsButtonSet` is not authoritative there
  // and the aiFashion slice's `ui.suggestionsButtonSet` is read by the caller
  // (SuggestionsBar) to decide ghost vs. real rendering.
  if (aiFashionOverride) {
    if (aiFashionOverride.buttonSet === 'aiStudioVideo') {
      return aiStudioVideoButtonSet.getButtons(aiStudioVideoData, onActionSelect, triggerGeneration)
    }
    return getAIFashionButtons(aiFashionData, triggerGeneration)
  }
  if (activeKey === 'aiFashion') return getAIFashionButtons(aiFashionData, triggerGeneration)
  if (activeKey === 'aiStudioVideo') {
    return aiStudioVideoButtonSet.getButtons(aiStudioVideoData, onActionSelect, triggerGeneration)
  }

  return []
}
